import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import {
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Refresh, Timelapse } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import FilteredListLayout from '../components/FilteredListLayout';
import ListActionButton from '../components/ListActionButton';
import { RequirePermission } from '../contexts/PermissionContext';
import { useNumberFormat } from '../hooks/useNumberFormat';
import usePageTitle from '../hooks/usePageTitle';
import { deleteMinutePackAdmin, fetchMinutePacksAdmin } from '../services/subscriptions';
import type { MinutePackCatalog } from '../types';
import { useListSearchParams } from '../hooks/useListSearchParams';
import LinkPagination from '../components/LinkPagination';
import LinkTableCell from '../components/LinkTableCell';

export default function MinutePacksAdminPage() {
  const { t } = useTranslation(['pages', 'common']);
  usePageTitle(t('pages:minutePacks.title'));
  const { formatCurrency, formatNumber } = useNumberFormat();
  const [packs, setPacks] = useState<MinutePackCatalog[]>([]);
  const [loading, setLoading] = useState(true);
  const {
    filters,
    page,
    rowsPerPage,
    ordering,
    setParams,
    setPage,
    setRowsPerPage,
    getPageLink,
  } = useListSearchParams({
    filters: ['search'],
    defaultPage: 1,
    defaultPageSize: 10,
    defaultOrdering: 'type',
  });

  const searchValue = filters.search ?? '';
  const orderingParam = ordering || 'type';

  const loadPacks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMinutePacksAdmin();
      setPacks(data);
    } catch (error) {
      console.error('Error loading minute packs:', error);
      setPacks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPacks();
  }, [loadPacks]);

  const handleDelete = async (packId: string) => {
    if (!confirm(t('pages:minutePacks.deleteConfirm'))) {
      return;
    }
    try {
      await deleteMinutePackAdmin(packId);
      loadPacks();
    } catch (error) {
      console.error('Error deleting minute pack:', error);
    }
  };

  const headerActions = (
    <>
      <ListActionButton variant="outlined" startIcon={<Refresh />} onClick={loadPacks}>
        {t('common:actions.refresh')}
      </ListActionButton>
      <RequirePermission permission="subscriptions_packs_create">
        <ListActionButton
          variant="contained"
          startIcon={<Add />}
          component={RouterLink}
          to="/subscriptions/minute-packs/new"
        >
          {t('pages:minutePacks.new')}
        </ListActionButton>
      </RequirePermission>
    </>
  );

  const filteredPacks = useMemo(() => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    const list = normalizedSearch
      ? packs.filter((pack) =>
          `${pack.type} ${pack.minutesQuantity}`.toLowerCase().includes(normalizedSearch)
        )
      : packs;

    return [...list].sort((a, b) => {
      if (orderingParam === '-type') {
        const typeComparison = b.type.localeCompare(a.type);
        if (typeComparison !== 0) {
          return typeComparison;
        }
        return b.minutesQuantity - a.minutesQuantity;
      }
      const typeComparison = a.type.localeCompare(b.type);
      if (typeComparison !== 0) {
        return typeComparison;
      }
      return a.minutesQuantity - b.minutesQuantity;
    });
  }, [orderingParam, packs, searchValue]);

  const paginatedPacks = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredPacks.slice(start, start + rowsPerPage);
  }, [filteredPacks, page, rowsPerPage]);

  const filterControls = (
    <TextField
      size="small"
      label={t('common:actions.search')}
      placeholder={t('pages:minutePacks.filters.searchPlaceholder')}
      value={searchValue}
      onChange={(event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setParams({ search: value || undefined }, { resetPage: true });
      }}
      sx={{ minWidth: 240, flex: '1 1 240px' }}
    />
  );

  const table = (
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>{t('pages:minutePacks.columns.type')}</TableCell>
          <TableCell>{t('pages:minutePacks.columns.quantity')}</TableCell>
          <TableCell>{t('pages:minutePacks.columns.price')}</TableCell>
          <TableCell>{t('pages:minutePacks.columns.status')}</TableCell>
          <TableCell align="right">{t('common:actions.actions')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {loading ? (
          <TableRow>
            <TableCell colSpan={5}>
              <Typography color="text.secondary">{t('common:common.loading')}</Typography>
            </TableCell>
          </TableRow>
        ) : filteredPacks.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5}>
              <Typography color="text.secondary">{t('common:common.noData')}</Typography>
            </TableCell>
          </TableRow>
        ) : (
          paginatedPacks.map((pack) => (
            <TableRow key={pack.id} hover>
              <LinkTableCell to={`/subscriptions/minute-packs/${pack.id}`}>
                {t(`pages:subscription.packTypes.${pack.type}`)}
              </LinkTableCell>
              <TableCell>{formatNumber(pack.minutesQuantity)}</TableCell>
              <TableCell>{formatCurrency(Number(pack.priceAmount), pack.priceCurrency, 2)}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={pack.isActive ? 'success' : 'default'}
                  label={pack.isActive ? t('common:status.active') : t('common:status.inactive')}
                />
              </TableCell>
              <TableCell align="right">
                <RequirePermission permission="subscriptions_packs_update">
                  <IconButton
                    component={RouterLink}
                    to={`/subscriptions/minute-packs/${pack.id}`}
                    size="small"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </RequirePermission>
                <RequirePermission permission="subscriptions_packs_delete">
                  <IconButton size="small" color="error" onClick={() => handleDelete(pack.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </RequirePermission>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );

  return (
    <FilteredListLayout
      headerProps={{
        icon: <Timelapse />,
        title: t('pages:minutePacks.title'),
        subtitle: t('pages:minutePacks.subtitle'),
        actions: headerActions,
      }}
      filterControls={filterControls}
      table={table}
      tableFooter={(
        <LinkPagination
          count={filteredPacks.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => setRowsPerPage(Number(event.target.value))}
          getPageLink={getPageLink}
        />
      )}
    />
  );
}
